import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import infoImg from "../../assets/images/info.svg";
import profileImg from "../../assets/images/profile.svg";
import { getCurrentUserProfile } from "../../services/dashboardService";
import { savePayoutMethod, validateBankAccount } from "../../services/wiseService";
import { getCreditPackages, createCreditTopup, confirmCreditTopup } from "../../services/creditsService";
import './settings.scss';
import gamingAvatar1 from "../../assets/avatars/gaming-avatar-1.svg";
import gamingAvatar2 from "../../assets/avatars/gaming-avatar-2.svg";
import gamingAvatar3 from "../../assets/avatars/gaming-avatar-3.svg";
import gamingAvatar4 from "../../assets/avatars/gaming-avatar-4.svg";
import gamingAvatar5 from "../../assets/avatars/gaming-avatar-5.svg";
import gamingAvatar6 from "../../assets/avatars/gaming-avatar-6.svg";

function Settings() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('account');
  const [loading, setLoading] = useState(false);
  const [creditPackages, setCreditPackages] = useState([]);
  const [topupLoadingId, setTopupLoadingId] = useState(null);
  const [topupStatus, setTopupStatus] = useState(null);
  const fileInputRef = useRef(null);
  const AVATAR_OPTIONS = [
    { id: 'gaming1', label: 'Purple Gamer', src: gamingAvatar1 },
    { id: 'gaming2', label: 'Blue Pixel', src: gamingAvatar2 },
    { id: 'gaming3', label: 'Green Champion', src: gamingAvatar3 },
    { id: 'gaming4', label: 'Orange Warrior', src: gamingAvatar4 },
    { id: 'gaming5', label: 'Violet Pro', src: gamingAvatar5 },
    { id: 'gaming6', label: 'Cyan Elite', src: gamingAvatar6 },
  ];
  const DEFAULT_AVATAR = AVATAR_OPTIONS[0].src;
  const [avatarPreview, setAvatarPreview] = useState(DEFAULT_AVATAR);
  const [customAvatarFile, setCustomAvatarFile] = useState(null);
  const [userProfile, setUserProfile] = useState({
    name: '',
    username: '',
    email: '',
    role: 'playtester',
    avatar: DEFAULT_AVATAR,
    preferences: {
      emailNotifications: true,
      gameInvites: true,
      publicProfile: false
    },
    payoutMethods: {
      paypalEmail: '',
      bankName: '',
      accountName: '',
      sortCode: '',
      accountNumber: '',
      swiftCode: '',
      iban: '',
      payoutProvider: 'wise',
      country: '',
      currency: '',
      accountLast4: ''
    }
  });

  useEffect(() => {
    // Load user profile
    const loadProfile = async () => {
      try {
        setLoading(true);
        const profile = await getCurrentUserProfile();
        if (profile) {
          const storedPrefs = profile.notificationPreferences || profile.preferences || {};
          const parsedPrefs = typeof storedPrefs === "string"
            ? (() => { try { return JSON.parse(storedPrefs); } catch { return {}; } })()
            : storedPrefs;
          setUserProfile(prev => ({
            ...prev,
            ...profile,
            name: (profile.name || '').replace(/\bundefined\b/gi, '').replace(/\bnull\b/gi, '').replace(/\s+/g, ' ').trim(),
            username: profile.username || '',
            avatar: profile.avatar || prev.avatar || DEFAULT_AVATAR,
            // Merge defaults if missing
            preferences: {
              emailNotifications: true,
              gameInvites: true,
              publicProfile: false,
              ...prev.preferences,
              ...(parsedPrefs || {}),
            },
            payoutMethods: { ...prev.payoutMethods, ...(profile.payoutMethods || {}) }
          }));
          setAvatarPreview(profile.avatar || DEFAULT_AVATAR);
          if (profile.role === "developer") {
            setActiveSection((current) => (current === "payout" ? "credits" : current));
            try {
              const catalog = await getCreditPackages();
              setCreditPackages(catalog.packages || []);
            } catch (packageError) {
              setCreditPackages([
                { id: "credits_30", pay: 30, credits: 30, bonusCredits: 0, label: "30 Credits" },
                { id: "credits_150", pay: 150, credits: 150, bonusCredits: 0, label: "150 Credits" },
                { id: "credits_750", pay: 550, credits: 750, bonusCredits: 100, label: "750 Credits", popular: true },
                { id: "credits_1200", pay: 800, credits: 1200, bonusCredits: 200, label: "1,200 Credits", bestValue: true },
              ]);
            }
          }
        }
      } catch (error) {
        // console.error("Error loading profile:", error);
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const topup = params.get("topup");
    if (topup !== "success" && topup !== "cancelled") {
      return undefined;
    }

    setActiveSection("credits");

    const sessionId = params.get("session_id");

    params.delete("topup");
    params.delete("session_id");
    const cleanSearch = params.toString();
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ""}`,
    );

    if (topup === "cancelled") {
      setTopupStatus({ type: "info", message: "Checkout was cancelled." });
      return undefined;
    }

    if (!sessionId) {
      setTopupStatus({
        type: "error",
        message: "Payment session not found. Contact support if you were charged.",
      });
      return undefined;
    }

    let cancelled = false;

    const applyCreditsBalance = (creditsEarned) => {
      setUserProfile((prev) => ({
        ...prev,
        creditsEarned,
      }));

      const storedUserData = localStorage.getItem("userData");
      if (storedUserData) {
        try {
          const parsed = JSON.parse(storedUserData);
          localStorage.setItem(
            "userData",
            JSON.stringify({ ...parsed, creditsEarned }),
          );
        } catch {
          // Ignore malformed cached user data.
        }
      }
    };

    (async () => {
      setTopupStatus({ type: "success", message: "Payment received. Updating your balance..." });

      try {
        const result = await confirmCreditTopup(sessionId);
        if (cancelled) {
          return;
        }

        applyCreditsBalance(Number(result.creditsEarned || 0));
        setTopupStatus({
          type: "success",
          message: result.alreadyFulfilled
            ? "Payment successful. Your credits are up to date."
            : `Payment successful. ${Number(result.creditsAwarded || 0).toLocaleString()} credits added.`,
        });
      } catch (error) {
        if (cancelled) {
          return;
        }
        setTopupStatus({
          type: "error",
          message: error.response?.data?.message || "Unable to confirm payment. Refresh the page or contact support.",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleTopup = async (packageId) => {
    try {
      setTopupLoadingId(packageId);
      setTopupStatus(null);
      const result = await createCreditTopup(packageId);
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      setTopupStatus({
        type: "error",
        message: result.message || "Unable to start checkout. Please try again.",
      });
    } catch (error) {
      setTopupStatus({
        type: "error",
        message: error.response?.data?.message || "Unable to start checkout. Please try again.",
      });
    } finally {
      setTopupLoadingId(null);
    }
  };

  const handleInputChange = (e, section = null) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;

    if (section) {
      setUserProfile(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [name]: val
        }
      }));
    } else {
      setUserProfile(prev => ({
        ...prev,
        [name]: val
      }));
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Prepare form data for profile update (including avatar)
      const formData = new FormData();
      
      // Add profile fields
      formData.append('name', userProfile.name || '');
      if ((userProfile.username || '').trim()) {
        formData.append('username', userProfile.username.trim());
      }
      if (userProfile.role === 'developer' && (userProfile.email || '').trim()) {
        formData.append('email', userProfile.email.trim());
      }
      formData.append('notificationPreferences', JSON.stringify(userProfile.preferences || {}));
      
      // Add avatar if custom file was uploaded
      if (customAvatarFile) {
        formData.append('avatar', customAvatarFile);
      } else if (
        userProfile.avatar &&
        !userProfile.avatar.startsWith('blob:') &&
        !userProfile.avatar.startsWith('http')
      ) {
        // Preset avatar path selected in the UI
        formData.append('avatar', userProfile.avatar);
      }
      
      // If payout methods are being saved
      if (activeSection === 'payout') {
        const payoutData = userProfile.payoutMethods;
        
        if (payoutData.payoutProvider === 'wise') {
          // Validate Wise account details
          if (payoutData.accountName && payoutData.accountNumber && payoutData.country && payoutData.currency) {
            try {
              await validateBankAccount({
                accountName: payoutData.accountName,
                accountNumber: payoutData.accountNumber,
                sortCode: payoutData.sortCode,
                swiftCode: payoutData.swiftCode,
                iban: payoutData.iban,
                bankName: payoutData.bankName,
                country: payoutData.country,
                currency: payoutData.currency,
              });
            } catch (error) {
              alert('Account validation failed. Please check your bank details.');
              setLoading(false);
              return;
            }
          }
        }
        
        // Save payout method
        await savePayoutMethod(payoutData);
      }
      
      // Update profile via API
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || "https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1"}/profile`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        }
      );
      
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const message = Array.isArray(body.message) ? body.message.join(' ') : body.message;
        throw new Error(message || 'Failed to save profile');
      }
      
      const updatedProfile = await response.json();
      
      // Update local state with new avatar if returned
      if (updatedProfile.avatar) {
        setAvatarPreview(updatedProfile.avatar);
        setUserProfile(prev => ({ ...prev, avatar: updatedProfile.avatar }));
      }
      if (updatedProfile.name) {
        localStorage.setItem('name', updatedProfile.name);
        setUserProfile(prev => ({ ...prev, name: updatedProfile.name }));
      }
      if (updatedProfile.username) {
        localStorage.setItem('username', updatedProfile.username);
        setUserProfile(prev => ({ ...prev, username: updatedProfile.username }));
      }
      if (updatedProfile.email) {
        localStorage.setItem('immerselyEmail', updatedProfile.email);
        setUserProfile(prev => ({ ...prev, email: updatedProfile.email }));
        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
          try {
            const parsed = JSON.parse(storedUserData);
            localStorage.setItem('userData', JSON.stringify({ ...parsed, email: updatedProfile.email }));
          } catch {
            // Ignore malformed cached user data.
          }
        }
      }
      
      alert('Settings saved successfully!');
    } catch (error) {
      alert(error.message || 'Failed to save settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (userProfile.role !== 'playtester') return;
    if (!window.confirm('Are you sure?')) return;
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL || "https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1"}/profile`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    if (!response.ok) return;
    handleLogout();
    alert('The account has been deleted.');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('name');
    localStorage.removeItem('username');
    localStorage.removeItem('immerselyEmail');
    localStorage.removeItem('userId');
    localStorage.removeItem('userData');
    navigate('/');
  };

  const handleAvatarSelect = (src) => {
    setCustomAvatarFile(null);
    setAvatarPreview(src);
    setUserProfile(prev => ({ ...prev, avatar: src }));
  };

  const handleAvatarUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const tempUrl = URL.createObjectURL(file);
    setCustomAvatarFile(file);
    setAvatarPreview(tempUrl);
    setUserProfile(prev => ({ ...prev, avatar: tempUrl }));
  };

  const triggerAvatarUpload = () => {
    fileInputRef.current?.click();
  };

  const homePath =
    userProfile.role === "developer"
      ? "/developer"
      : userProfile.role === "admin"
        ? "/admin"
        : "/dashboard";

  return (
    <div className="settings-page">
      <header className="settings-topbar">
        <button type="button" className="settings-brand" onClick={() => navigate(homePath)}>
          <img src={infoImg} alt="" />
          <span>IMMERSELY</span>
          <em>SETTINGS</em>
        </button>
        <div className="settings-topbar-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate(homePath)}>
            Back
          </button>
          <div className="settings-avatar-wrap">
            <button type="button" className="settings-avatar-btn" aria-label="Account menu">
              <img
                src={avatarPreview || profileImg}
                alt=""
                onError={(e) => {
                  e.target.src = profileImg;
                }}
              />
            </button>
            <div className="settings-avatar-menu">
              <button type="button" onClick={() => navigate(homePath)}>
                Dashboard
              </button>
              <button type="button" onClick={handleLogout}>
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="settings-container">
        <div className="settings-header">
          <h1>Settings</h1>
          <p>Manage your account settings and preferences.</p>
        </div>

        <div className="settings-grid">
          {/* Settings Navigation */}
          <nav className="settings-nav">
            <button 
              className={`nav-item ${activeSection === 'account' ? 'active' : ''}`}
              onClick={() => setActiveSection('account')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              Account Management
            </button>
            <button 
              className={`nav-item ${activeSection === 'preferences' ? 'active' : ''}`}
              onClick={() => setActiveSection('preferences')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.1a2 2 0 0 1-1-1.72v-.51a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
              Preferences
            </button>
            {userProfile.role === "developer" && (
              <button
                className={`nav-item ${activeSection === "credits" ? "active" : ""}`}
                onClick={() => setActiveSection("credits")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 6v12M8 10h8M8 14h8"></path>
                </svg>
                Credits
              </button>
            )}
            {userProfile.role !== "developer" && (
            <button 
              className={`nav-item ${activeSection === 'payout' ? 'active' : ''}`}
              onClick={() => setActiveSection('payout')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                <line x1="1" y1="10" x2="23" y2="10"></line>
              </svg>
              Payout Methods
            </button>
            )}
            <button 
              className={`nav-item ${activeSection === 'security' ? 'active' : ''}`}
              onClick={() => setActiveSection('security')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
              Security
            </button>
          </nav>

          {/* Content Area */}
          <div className="settings-content">
            {activeSection === 'account' && (
              <>
                <h2>Account Management</h2>
                
                <div className="avatar-section">
                  <div className="current-avatar">
                    <img src={avatarPreview || profileImg} alt="profile" onError={(e) => { e.target.src = profileImg; }} />
                  </div>
                  <div className="avatar-actions">
                    <button 
                      className="btn btn-primary" 
                      type="button"
                      onClick={triggerAvatarUpload}
                    >
                      Upload Custom
                    </button>
                    <button 
                      className="btn btn-secondary" 
                      type="button"
                      onClick={() => handleAvatarSelect(DEFAULT_AVATAR)}
                    >
                      Reset
                    </button>
                  </div>
                  <input 
                    type="file" 
                    accept="image/png,image/jpeg,image/svg+xml"
                    ref={fileInputRef}
                    className="sr-only"
                    onChange={handleAvatarUpload}
                  />
                </div>

                <div className="avatar-options">
                  {AVATAR_OPTIONS.map(option => (
                    <button
                      type="button"
                      key={option.id}
                      className={`avatar-option ${avatarPreview === option.src ? 'selected' : ''}`}
                      onClick={() => handleAvatarSelect(option.src)}
                    >
                      <img src={option.src} alt={option.label} />
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>

                <div className="form-group">
                  <label>Display Name</label>
                  <input 
                    type="text" 
                    name="name"
                    className="form-control" 
                    value={userProfile.name || ''}
                    onChange={handleInputChange}
                    placeholder="Enter your name"
                  />
                </div>

                <div className="form-group">
                  <label>Username</label>
                  <input 
                    type="text" 
                    name="username"
                    className="form-control" 
                    value={userProfile.username || ''}
                    onChange={handleInputChange}
                    placeholder="@username"
                  />
                </div>

                <div className="form-group">
                  <label>Email Address</label>
                  <input 
                    type="email" 
                    name="email"
                    className="form-control" 
                    value={userProfile.email || ''}
                    onChange={userProfile.role === 'developer' ? handleInputChange : undefined}
                    disabled={userProfile.role !== 'developer'}
                  />
                  <span className="helper-text">
                    {userProfile.role === 'developer'
                      ? 'This is the email used to sign in to your developer account.'
                      : 'Contact support to change email'}
                  </span>
                </div>

                <h3>Linked Accounts</h3>
                <div className="connected-accounts">
                  <div className="account-item">
                    <div className="account-info">
                      <div className="account-icon">
                        <svg width="22" height="22" viewBox="0 0 245 240" fill="currentColor">
                          <path d="M104.4 104.8c-5.7 0-10.2 5-10.2 11.1s4.6 11.1 10.2 11.1c5.7 0 10.2-5 10.2-11.1.1-6.1-4.5-11.1-10.2-11.1zm36.2 0c-5.7 0-10.2 5-10.2 11.1s4.6 11.1 10.2 11.1c5.7 0 10.2-5 10.2-11.1s-4.5-11.1-10.2-11.1z"/>
                          <path d="M189.5 20h-134C41.4 20 30 31.4 30 45.5v134c0 14.1 11.4 25.5 25.5 25.5h114.3l-5.4-19 13 12.1 12.3 11.5 21.8 19.9V45.5c.1-14.1-11.3-25.5-25.5-25.5zm-39.2 135.6s-3.7-4.4-6.8-8.3c13.5-3.8 18.6-12.1 18.6-12.1-4.2 2.7-8.2 4.6-11.8 5.9-5.1 2.1-10 3.5-14.8 4.3-9.8 1.8-18.8 1.3-26.4-.1-5.8-1.1-10.8-2.7-15-4.3-2.3-.9-4.8-2-7.3-3.4-.3-.2-.6-.3-.9-.5-.2-.1-.3-.2-.5-.3-2.1-1.2-3.2-2-3.2-2s5 8.1 18.3 12c-3.1 3.9-6.9 8.5-6.9 8.5-22.8-.7-31.5-15.7-31.5-15.7 0-33.2 14.8-60.1 14.8-60.1 14.8-11 28.8-10.7 28.8-10.7l1 1.2c-18.5 5.3-27 13.3-27 13.3s2.2-1.2 5.9-2.9c10.7-4.7 19.2-6 22.7-6.3.6-.1 1.1-.2 1.7-.2 6.1-.8 12.9-1 20.2-.2 9.5 1.1 19.7 3.9 30.1 9.6 0 0-8.1-7.7-25.8-12.9l1.4-1.6s13.9-.3 28.8 10.7c0 0 14.8 26.9 14.8 60.1 0 .1-8.7 15-31.6 15.7z"/>
                        </svg>
                      </div>
                      <div className="account-details">
                        <h4>Discord</h4>
                        <p>Keep your playtester handle in sync</p>
                      </div>
                    </div>
                    <button className="btn btn-secondary">Connect</button>
                  </div>
                  
                  <div className="account-item">
                    <div className="account-info">
                      <div className="account-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M11.53 17.5c-.32 1.38-.28 3.15 1.35 4.54 1.24 1.06 2.85 1.06 3.75 1.06 3.65 0 6.6-2.98 6.6-6.64 0-3.66-2.95-6.63-6.6-6.63-1.6 0-3.08.56-4.24 1.5-.96.79-1.66 1.85-1.98 3.03l-4.9-2.03c.53-2.06 2-3.8 4.02-4.7C10.63 6.96 11.9 6.54 13.23 6.54c4.75 0 8.6 3.86 8.6 8.62 0 4.76-3.85 8.62-8.6 8.62-2.18 0-4.2-.76-5.78-2.03-2.3-1.85-2.82-4.57-2.35-6.52L11.53 17.5zM2.5 12c0-5.25 4.25-9.5 9.5-9.5 1.76 0 3.4.5 4.8 1.38l1.6-1.6C16.63 1.14 14.4 0 12 0 5.37 0 0 5.37 0 12c0 2.2.62 4.25 1.7 6l1.83-1.38C2.88 15.35 2.5 13.73 2.5 12z"/></svg>
                      </div>
                      <div className="account-details">
                        <h4>Steam</h4>
                        <p>Connected as {userProfile.username}</p>
                      </div>
                    </div>
                    <button className="btn btn-secondary">Disconnect</button>
                  </div>
                </div>
                {userProfile.role === 'playtester' && (
                  <button type="button" className="btn btn-delete-account" onClick={handleDeleteAccount}>
                    Delete account
                  </button>
                )}
              </>
            )}

            {activeSection === 'preferences' && (
              <>
                <h2>Preferences</h2>
                
                <div className="form-group">
                  {userProfile.role === "playtester" ? (
                    <>
                      <h3>Notifications</h3>
                      <div className="toggle-group">
                        <div className="toggle-info">
                          <h3>Game Invites</h3>
                          <p>Email me when an admin assigns me to a playtest.</p>
                        </div>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            name="gameInvites"
                            checked={userProfile.preferences?.gameInvites !== false}
                            onChange={(e) => handleInputChange(e, "preferences")}
                          />
                          <span className="slider"></span>
                        </label>
                      </div>
                    </>
                  ) : (
                    <>
                      <h3>Notifications</h3>
                      <p className="helper-text" style={{ color: "#9ca3af", marginBottom: "1rem" }}>
                        Password reset emails are always sent. Optional product emails are not enabled for this role yet.
                      </p>
                    </>
                  )}
                </div>

                {userProfile.role === "playtester" && (
                  <div className="form-group">
                    <h3>Privacy</h3>
                    <div className="toggle-group">
                      <div className="toggle-info">
                        <h3>Public Profile</h3>
                        <p>Allow others to see your gaming stats and achievements</p>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          name="publicProfile"
                          checked={userProfile.preferences?.publicProfile}
                          onChange={(e) => handleInputChange(e, "preferences")}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  </div>
                )}
              </>
            )}

            {activeSection === "credits" && userProfile.role === "developer" && (
              <>
                <h2>Credits</h2>
                <p style={{ color: "#9ca3af", marginBottom: "1.5rem" }}>
                  Top up your Immersely credit balance via Stripe Checkout.
                </p>
                {topupStatus && (
                  <p style={{
                    color: topupStatus.type === "error" ? "#f87171" : topupStatus.type === "success" ? "#4ade80" : "#93c5fd",
                    marginBottom: "1rem",
                  }}>
                    {topupStatus.message}
                  </p>
                )}
                <div className="credit-balance-card">
                  <span>Current balance</span>
                  <strong>{Number(userProfile.creditsEarned || 0).toLocaleString()} credits</strong>
                </div>
                <div className="credit-packages">
                  {creditPackages.map((pkg) => (
                    <div key={pkg.id} className="credit-package">
                      <div>
                        <h4>{pkg.label}</h4>
                        <p>
                          Pay £{pkg.pay}
                          {pkg.bonusCredits ? ` · +${pkg.bonusCredits} bonus` : ""}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={topupLoadingId === pkg.id}
                        onClick={() => handleTopup(pkg.id)}
                      >
                        {topupLoadingId === pkg.id ? "Redirecting..." : `Buy for £${pkg.pay}`}
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {activeSection === "payout" && userProfile.role !== "developer" && (
              <>
                <h2>Payout Methods</h2>
                <p style={{ color: '#9ca3af', marginBottom: '2rem' }}>
                  Manage how you receive payments for your playtesting sessions. We use Wise Business for secure, low-cost international transfers.
                </p>

                <div className="form-group">
                  <label>Payout Provider *</label>
                  <select
                    name="payoutProvider"
                    className="form-control"
                    value={userProfile.payoutMethods?.payoutProvider || 'wise'}
                    onChange={(e) => handleInputChange(e, 'payoutMethods')}
                    required
                  >
                    <option value="wise">Wise (Recommended - Multi-currency, low fees)</option>
                    <option value="paypal">PayPal (Global)</option>
                  </select>
                  <span className="helper-text">
                    Wise Business handles international transfers, currency conversion, and compliance automatically. 
                    PayPal is available as an alternative option.
                  </span>
                </div>

                {userProfile.payoutMethods?.payoutProvider === 'wise' && (
                  <>
                    <div style={{ marginBottom: '2rem', padding: '1rem', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '0.5rem' }}>
                      <h4 style={{ color: '#60a5fa', marginTop: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                        Wise Business Integration
                      </h4>
                      <p style={{ color: '#d1d5db', fontSize: '0.875rem', margin: '0.5rem 0 0 0' }}>
                        Your bank account details will be securely stored in our database and used to create Wise recipients for payout requests. 
                        Wise stores recipient information securely on their platform. We support transfers to 160+ countries with real exchange rates and low fees.
                      </p>
                    </div>

                    <div className="form-group">
                      <label>Country *</label>
                      <select
                        name="country"
                        className="form-control"
                        value={userProfile.payoutMethods?.country || ''}
                        onChange={(e) => handleInputChange(e, 'payoutMethods')}
                        required={userProfile.payoutMethods?.payoutProvider === 'wise'}
                      >
                        <option value="">Select Country</option>
                        <option value="US">United States</option>
                        <option value="GB">United Kingdom</option>
                        <option value="CA">Canada</option>
                        <option value="AU">Australia</option>
                        <option value="DE">Germany</option>
                        <option value="FR">France</option>
                        <option value="ES">Spain</option>
                        <option value="IT">Italy</option>
                        <option value="NL">Netherlands</option>
                        <option value="SE">Sweden</option>
                        <option value="NO">Norway</option>
                        <option value="DK">Denmark</option>
                        <option value="PL">Poland</option>
                        <option value="BR">Brazil</option>
                        <option value="MX">Mexico</option>
                        <option value="IN">India</option>
                        <option value="SG">Singapore</option>
                        <option value="JP">Japan</option>
                        <option value="NZ">New Zealand</option>
                        <option value="CH">Switzerland</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Currency *</label>
                      <select
                        name="currency"
                        className="form-control"
                        value={userProfile.payoutMethods?.currency || ''}
                        onChange={(e) => handleInputChange(e, 'payoutMethods')}
                        required={userProfile.payoutMethods?.payoutProvider === 'wise'}
                      >
                        <option value="">Select Currency</option>
                        <option value="USD">USD - US Dollar</option>
                        <option value="GBP">GBP - British Pound</option>
                        <option value="EUR">EUR - Euro</option>
                        <option value="CAD">CAD - Canadian Dollar</option>
                        <option value="AUD">AUD - Australian Dollar</option>
                        <option value="JPY">JPY - Japanese Yen</option>
                        <option value="CHF">CHF - Swiss Franc</option>
                        <option value="SEK">SEK - Swedish Krona</option>
                        <option value="NOK">NOK - Norwegian Krone</option>
                        <option value="DKK">DKK - Danish Krone</option>
                        <option value="PLN">PLN - Polish Zloty</option>
                        <option value="BRL">BRL - Brazilian Real</option>
                        <option value="MXN">MXN - Mexican Peso</option>
                        <option value="INR">INR - Indian Rupee</option>
                        <option value="SGD">SGD - Singapore Dollar</option>
                        <option value="NZD">NZD - New Zealand Dollar</option>
                      </select>
                    </div>
                  </>
                )}

                {userProfile.payoutMethods?.payoutProvider === 'paypal' && (
                  <>
                    <div style={{ marginBottom: '2rem', padding: '1rem', background: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.2)', borderRadius: '0.5rem' }}>
                      <h4 style={{ color: '#fbbf24', marginTop: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                        PayPal Integration
                      </h4>
                      <p style={{ color: '#d1d5db', fontSize: '0.875rem', margin: '0.5rem 0 0 0' }}>
                        Payments will be sent directly to your PayPal account. Make sure your PayPal email is verified and can receive payments.
                      </p>
                    </div>

                    <div className="form-group">
                      <label>PayPal Email Address *</label>
                      <input 
                        type="email" 
                        name="paypalEmail"
                        className="form-control" 
                        placeholder="name@example.com"
                        value={userProfile.payoutMethods?.paypalEmail || ''}
                        onChange={(e) => handleInputChange(e, 'payoutMethods')}
                        required={userProfile.payoutMethods?.payoutProvider === 'paypal'}
                      />
                      <span className="helper-text">Enter your verified PayPal email address</span>
                    </div>
                  </>
                )}

                <div className="form-group two-column">
                  <div>
                    <label>Bank Name</label>
                    <input 
                      type="text" 
                      name="bankName"
                      className="form-control" 
                      placeholder="Bank Name"
                      value={userProfile.payoutMethods?.bankName || ''}
                      onChange={(e) => handleInputChange(e, 'payoutMethods')}
                    />
                  </div>
                  <div>
                    <label>Account Holder Name</label>
                    <input 
                      type="text" 
                      name="accountName"
                      className="form-control" 
                      placeholder="As it appears on the statement"
                      value={userProfile.payoutMethods?.accountName || ''}
                      onChange={(e) => handleInputChange(e, 'payoutMethods')}
                    />
                  </div>
                </div>

                <div className="form-group two-column">
                  <div>
                    <label>Sort Code / Routing Number</label>
                    <input 
                      type="text" 
                      name="sortCode"
                      className="form-control" 
                      placeholder="00-00-00"
                      value={userProfile.payoutMethods?.sortCode || ''}
                      onChange={(e) => handleInputChange(e, 'payoutMethods')}
                    />
                  </div>
                  <div>
                    <label>Account Number</label>
                    <input 
                      type="text" 
                      name="accountNumber"
                      className="form-control" 
                      placeholder="00000000"
                      value={userProfile.payoutMethods?.accountNumber || ''}
                      onChange={(e) => handleInputChange(e, 'payoutMethods')}
                    />
                  </div>
                </div>

                <div className="form-group two-column">
                  <div>
                    <label>SWIFT / BIC</label>
                    <input 
                      type="text" 
                      name="swiftCode"
                      className="form-control" 
                      placeholder="BANKGB22"
                      value={userProfile.payoutMethods?.swiftCode || ''}
                      onChange={(e) => handleInputChange(e, 'payoutMethods')}
                    />
                  </div>
                  <div>
                    <label>IBAN</label>
                    <input 
                      type="text" 
                      name="iban"
                      className="form-control" 
                      placeholder="GB00 WEST 1234 5678 90"
                      value={userProfile.payoutMethods?.iban || ''}
                      onChange={(e) => handleInputChange(e, 'payoutMethods')}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Payout Provider</label>
                  <select
                    name="payoutProvider"
                    className="form-control"
                    value={userProfile.payoutMethods?.payoutProvider || 'paypal'}
                    onChange={(e) => handleInputChange(e, 'payoutMethods')}
                  >
                    <option value="paypal">PayPal (global)</option>
                    <option value="wise">Wise (multi-currency)</option>
                    <option value="payoneer">Payoneer (freelance payouts)</option>
                    <option value="stripe_connect">Stripe Connect</option>
                  </select>
                  <span className="helper-text">
                    We partner with third-party providers (Wise, Payoneer, Stripe Connect) so payouts stay compliant, tax forms are collected, and international wires arrive safely.
                  </span>
                </div>

                <div style={{ marginTop: '2rem', padding: '1rem', background: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.2)', borderRadius: '0.5rem' }}>
                  <h4 style={{ color: '#fbbf24', marginTop: 0 }}>Payout Information</h4>
                  <ul style={{ color: '#d1d5db', fontSize: '0.875rem', margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
                    <li>Minimum withdrawal amount is $20.00</li>
                    <li>Request payouts anytime from the <a href="/rewards" onClick={(e) => { e.preventDefault(); navigate('/rewards'); }}>Rewards</a> page</li>
                    <li>Wise transfers typically arrive within 1-2 business days</li>
                    <li>International transfers may take 3–5 business days depending on your bank</li>
                    <li>All transfers are processed through Wise Business for security and compliance</li>
                  </ul>
                </div>
              </>
            )}

            {activeSection === 'security' && (
              <>
                <h2>Security</h2>
                
                <div className="form-group">
                  <label>Current Password</label>
                  <input 
                    type="password" 
                    className="form-control" 
                    placeholder="••••••••"
                  />
                </div>

                <div className="form-group">
                  <label>New Password</label>
                  <input 
                    type="password" 
                    className="form-control" 
                    placeholder="Leave blank to keep current"
                  />
                </div>

                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input 
                    type="password" 
                    className="form-control" 
                    placeholder="Leave blank to keep current"
                  />
                </div>

                <div className="toggle-group" style={{ marginTop: '2rem' }}>
                  <div className="toggle-info">
                    <h3>Two-Factor Authentication (2FA)</h3>
                    <p>Coming soon. Extra account protection is not available yet.</p>
                  </div>
                  <button type="button" className="btn btn-secondary" disabled>
                    Coming soon
                  </button>
                </div>
              </>
            )}

            {activeSection !== "credits" && (
            <div className="action-buttons">
              <button className="btn btn-secondary" onClick={() => navigate(homePath)}>Cancel</button>
              <button 
                className="btn btn-primary" 
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;

